import { useState, useEffect, useMemo } from "react";
import { Form, Button, Row, Col, Spinner } from "react-bootstrap";
import api from "@/api/api.js";
import { useDropzone } from "react-dropzone";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const CreateBill = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userId = sessionStorage.getItem("user");

  const [companies, setCompanies] = useState([]);
  const [billingCompany, setBillingCompany] = useState("");

  // Solo las compañías del propio usuario (el backend ya filtra por dueño).
  useEffect(() => {
    api
      .get("/companies", { params: { itemsPerPage: 100 } })
      .then(({ data }) => {
        const list = data["hydra:member"] || [];
        setCompanies(list);
        setBillingCompany((prev) => prev || list[0]?.id || "");
      })
      .catch(() => setCompanies([]));
  }, []);

  // No se puede solicitar factura sin una compañía: redirige a crear una.
  useEffect(() => {
    const userId = sessionStorage.getItem("user");
    if (!userId) return;
    api
      .get(`/users/${userId}.json`)
      .then(({ data }) => {
        if (!data.companies || data.companies.length === 0) {
          Swal.fire({
            icon: "info",
            title: t("ui.accountNeededTitle"),
            text: t("ui.accountNeededText"),
          });
          navigate("/onboarding/company");
        }
      })
      .catch(() => {});
  }, [navigate, t]);

  /** -----------------------------
   *   CATÁLOGOS SAT (CFDI 4.0)
   *  ----------------------------- */
  const CFDI_USE_OPTIONS = useMemo(
    () => [
      { value: "G01", label: "G01 - Adquisición de mercancías" },
      {
        value: "G02",
        label: "G02 - Devoluciones, descuentos o bonificaciones",
      },
      { value: "G03", label: "G03 - Gastos en general" },
      { value: "I01", label: "I01 - Construcciones" },
      {
        value: "I02",
        label: "I02 - Mobiliario y equipo de oficina por inversiones",
      },
      { value: "I03", label: "I03 - Equipo de transporte" },
      { value: "I04", label: "I04 - Equipo de cómputo y accesorios" },
      {
        value: "I05",
        label: "I05 - Dados, troqueles, moldes, matrices y herramental",
      },
      { value: "I06", label: "I06 - Comunicaciones telefónicas" },
      { value: "I07", label: "I07 - Comunicaciones satelitales" },
      { value: "I08", label: "I08 - Otra maquinaria y equipo" },
      {
        value: "D01",
        label: "D01 - Honorarios médicos, dentales y gastos hospitalarios",
      },
      {
        value: "D02",
        label: "D02 - Gastos médicos por incapacidad o discapacidad",
      },
      { value: "D03", label: "D03 - Gastos funerales" },
      { value: "D04", label: "D04 - Donativos" },
      {
        value: "D05",
        label:
          "D05 - Intereses reales efectivamente pagados por créditos hipotecarios (casa habitación)",
      },
      { value: "D06", label: "D06 - Aportaciones voluntarias al SAR" },
      { value: "D07", label: "D07 - Primas por seguros de gastos médicos" },
      {
        value: "D08",
        label: "D08 - Gastos de transportación escolar obligatoria",
      },
      {
        value: "D09",
        label:
          "D09 - Depósitos en cuentas para el ahorro, primas con base en planes de pensiones",
      },
      {
        value: "D10",
        label: "D10 - Pagos por servicios educativos (colegiaturas)",
      },
      { value: "CP01", label: "CP01 - Pagos" },
      { value: "CN01", label: "CN01 - Nómina" },
      { value: "S01", label: "S01 - Sin efectos fiscales" },
    ],
    [],
  );

  const PAYMENT_METHOD_OPTIONS = useMemo(
    () => [
      { value: "PUE", label: "PUE - Pago en una sola exhibición" },
      { value: "PPD", label: "PPD - Pago en parcialidades o diferido" },
    ],
    [],
  );

  const PAYMENT_FORM_OPTIONS = useMemo(
    () => [
      { value: "01", label: "01 - Efectivo" },
      { value: "02", label: "02 - Cheque nominativo" },
      { value: "03", label: "03 - Transferencia electrónica de fondos" },
      { value: "04", label: "04 - Tarjeta de crédito" },
      { value: "05", label: "05 - Monedero electrónico" },
      { value: "06", label: "06 - Dinero electrónico" },
      { value: "08", label: "08 - Vales de despensa" },
      { value: "12", label: "12 - Dación en pago" },
      { value: "13", label: "13 - Pago por subrogación" },
      { value: "14", label: "14 - Pago por consignación" },
      { value: "15", label: "15 - Condonación" },
      { value: "17", label: "17 - Compensación" },
      { value: "23", label: "23 - Novación" },
      { value: "24", label: "24 - Confusión" },
      { value: "25", label: "25 - Remisión de deuda" },
      { value: "26", label: "26 - Prescripción o caducidad" },
      { value: "27", label: "27 - A satisfacción del acreedor" },
      { value: "28", label: "28 - Tarjeta de débito" },
      { value: "29", label: "29 - Tarjeta de servicios" },
      { value: "30", label: "30 - Aplicación de anticipos" },
      { value: "31", label: "31 - Intermediario pagos" },
      { value: "99", label: "99 - Por definir" },
    ],
    [],
  );

  /** -----------------------------
   *   STATE (todos requeridos)
   *  ----------------------------- */
  const [contactSearch, setContactSearch] = useState("");
  const [contactResults, setContactResults] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [contactFocused, setContactFocused] = useState(false);

  const [cfdiUse, setCfdiUse] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("PUE");
  const [paymentForm, setPaymentForm] = useState("03");
  const [currency, setCurrency] = useState("MXN"); // tu enum: MXN | DLS
  const [totalWithIva, setTotalWithIva] = useState("");
  const [iva, setIva] = useState("");
  const [descriptionsAndBillingConcepts, setDescriptionsAndBillingConcepts] =
    useState("");
  const [notes, setNotes] = useState("");

  const [attachment, setAttachment] = useState(null);
  const [layoutFile, setLayoutFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  /** -----------------------------
   *   AUTOCOMPLETE CONTACT SEARCH
   *  ----------------------------- */
  useEffect(() => {
    if (!userId) {
      setContactResults([]);
      return;
    }

    let cancel = false;
    const term = contactSearch.trim();
    const timeout = setTimeout(async () => {
      setLoadingContacts(true);
      try {
        const response = await api.get("/contacts/" + userId + "/all", {
          params: { itemsPerPage: 50, ...(term ? { search: term } : {}) },
        });

        if (!cancel) {
          const items =
            response.data["hydra:member"] ??
            response.data.items ??
            response.data;
          setContactResults(items || []);
        }
      } catch (err) {
        console.error(err);
        if (!cancel) setContactResults([]);
      } finally {
        if (!cancel) setLoadingContacts(false);
      }
    }, 300);

    return () => {
      cancel = true;
      clearTimeout(timeout);
    };
  }, [contactSearch, userId]);

  const handleContactSelect = (contact) => {
    setSelectedContact(contact);
    setContactSearch(`${contact.businessName} (${contact.taxIdentification})`);
    setContactResults([]);
  };

  /** -----------------------------
   *          DROPZONE
   *  ----------------------------- */
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple: false,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles && acceptedFiles.length > 0) {
        setAttachment(acceptedFiles[0]);
      }
    },
  });

  /** -----------------------------
   *   ERROR PARSER (API Platform/Symfony)
   *  ----------------------------- */
  const extractErrorMessage = (err) => {
    // Axios network error
    if (!err?.response) {
      return err?.message || "Error de red. Intenta nuevamente.";
    }

    const data = err.response.data;

    // API Platform often returns: { "hydra:description": "...", "hydra:title": "..." }
    if (data?.["hydra:description"]) return data["hydra:description"];

    // RFC7807: { detail, title }
    if (data?.detail) return data.detail;

    // Symfony Form errors if you returned (string)$form->getErrors()
    if (typeof data === "string") return data;

    // Constraint violations: { violations: [{propertyPath, message}] }
    if (Array.isArray(data?.violations) && data.violations.length > 0) {
      return data.violations
        .map((v) => `${v.propertyPath}: ${v.message}`)
        .join("\n");
    }

    // Fallback
    return `Error HTTP ${err.response.status}`;
  };

  /** -----------------------------
   *          FORM SUBMIT
   *  ----------------------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validaciones cliente (todos requeridos)
    if (!billingCompany) {
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: t("ui.selectAccountRequired"),
      });
      return;
    }
    if (!selectedContact) {
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: t("ui.selectClientRequired"),
      });
      return;
    }
    if (!cfdiUse) {
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: "Debes seleccionar el uso de CFDI.",
      });
      return;
    }
    if (!paymentMethod) {
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: "Debes seleccionar el método de pago.",
      });
      return;
    }
    if (!paymentForm) {
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: "Debes seleccionar la forma de pago.",
      });
      return;
    }
    if (!currency) {
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: "Debes seleccionar la moneda.",
      });
      return;
    }
    const hasComprobante = !!attachment;
    const hasLayout = !!layoutFile;
    const hasImporte =
      totalWithIva !== "" &&
      !isNaN(Number(totalWithIva)) &&
      iva !== "" &&
      !isNaN(Number(iva));
    const hasConceptos =
      !!descriptionsAndBillingConcepts &&
      descriptionsAndBillingConcepts.trim().length > 0;

    if (!hasComprobante && !hasLayout && !hasImporte) {
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: "Captura importe e IVA (con conceptos), o adjunta un comprobante, o sube un layout.",
      });
      return;
    }
    if (hasImporte && !hasConceptos) {
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: "Si capturas importe e IVA, debes capturar los conceptos de la factura.",
      });
      return;
    }

    const formData = new FormData();
    formData.append("companyId", billingCompany);
    formData.append("contactId", selectedContact.id);

    formData.append("cfdiUse", cfdiUse);
    formData.append("paymentMethod", paymentMethod);
    formData.append("paymentForm", paymentForm);
    formData.append("currency", currency); // MXN | DLS
    formData.append("totalWithIva", totalWithIva);
    formData.append("iva", iva);
    formData.append(
      "descriptionsAndBillingConcepts",
      descriptionsAndBillingConcepts,
    );
    formData.append("notes", notes);

    if (attachment) {
      formData.append("supportingDocument", attachment, attachment.name);
    }
    if (layoutFile) {
      formData.append("layout", layoutFile, layoutFile.name);
    }

    try {
      setSubmitting(true);

      await api.post("/bill-requests", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // éxito -> redirect
      navigate("/bills");
    } catch (err) {
      console.error(err);

      const message = extractErrorMessage(err);

      await Swal.fire({
        icon: "error",
        title: "No se pudo crear la factura",
        text: message,
        // Si tu backend manda saltos de línea, esto ayuda:
        didOpen: () => {
          const el = Swal.getHtmlContainer();
          if (el && typeof message === "string" && message.includes("\n")) {
            el.style.whiteSpace = "pre-wrap";
          }
        },
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="col">
      <div className="card p-3">
        <Form onSubmit={handleSubmit}>
          <Row className="g-3">
            {/* USUARIO QUE FACTURA (emisor en la BD: Company) */}
            <Col md={12}>
              <Form.Group controlId="billingCompany">
                <Form.Label>
                  {t("ui.selectAccountLabel")}
                  <span className="text-danger ms-1">*</span>
                </Form.Label>
                <Form.Select
                  value={billingCompany}
                  onChange={(e) => setBillingCompany(e.target.value)}
                  required
                >
                  <option value="">{t("ui.selectAccount")}</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                      {c.taxIdentification ? ` — ${c.taxIdentification}` : ""}
                    </option>
                  ))}
                </Form.Select>
                {billingCompany && (
                  <Form.Text className="text-muted">
                    Se facturará con:{" "}
                    {(() => {
                      const c = companies.find((x) => x.id === billingCompany);
                      return c
                        ? `${c.name}${c.taxIdentification ? ` — ${c.taxIdentification}` : ""}`
                        : "";
                    })()}
                  </Form.Text>
                )}
              </Form.Group>
            </Col>

            {/* CLIENTE (Contact en la BD) */}
            <Col md={12} className="position-relative">
              <Form.Group controlId="contactSearch">
                <Form.Label>
                  {t("ui.selectClientLabel")}
                  <span className="text-danger ms-1">*</span>
                </Form.Label>
                <div className="d-flex align-items-center gap-2">
                  <Form.Control
                    type="text"
                    placeholder="Buscar por razón social o RFC"
                    value={contactSearch}
                    onChange={(e) => {
                      setContactSearch(e.target.value);
                      setSelectedContact(null);
                    }}
                    onFocus={() => setContactFocused(true)}
                    onBlur={() =>
                      setTimeout(() => setContactFocused(false), 150)
                    }
                    autoComplete="off"
                    required
                  />
                  {loadingContacts && <Spinner animation="border" size="sm" />}
                </div>
              </Form.Group>

              {contactFocused && !selectedContact && (
                <div
                  className="list-group position-absolute w-100 shadow-sm bg-white"
                  style={{ zIndex: 10, maxHeight: "250px", overflowY: "auto" }}
                >
                  {contactResults.length === 0 ? (
                    <div className="list-group-item text-muted">
                      {loadingContacts
                        ? "Buscando..."
                        : t("ui.noClientsHint")}
                    </div>
                  ) : (
                    contactResults.map((contact) => (
                      <button
                        type="button"
                        key={contact.id}
                        className="list-group-item list-group-item-action"
                        onClick={() => handleContactSelect(contact)}
                      >
                        <div className="fw-bold">{contact.businessName}</div>
                        <small>RFC: {contact.taxIdentification}</small>
                      </button>
                    ))
                  )}
                </div>
              )}
            </Col>

            {/* USO CFDI */}
            <Col md={6}>
              <Form.Group controlId="cfdiUse">
                <Form.Label>
                  Uso de CFDI
                  <span className="text-danger ms-1">*</span>
                </Form.Label>
                <Form.Select
                  value={cfdiUse}
                  onChange={(e) => setCfdiUse(e.target.value)}
                  required
                >
                  <option value="">Seleccione uso de CFDI</option>
                  {CFDI_USE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            {/* MÉTODO DE PAGO */}
            <Col md={3}>
              <Form.Group controlId="paymentMethod">
                <Form.Label>
                  Método de pago
                  <span className="text-danger ms-1">*</span>
                </Form.Label>
                <Form.Select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  required
                >
                  {PAYMENT_METHOD_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            {/* FORMA DE PAGO */}
            <Col md={3}>
              <Form.Group controlId="paymentForm">
                <Form.Label>
                  Forma de pago
                  <span className="text-danger ms-1">*</span>
                </Form.Label>
                <Form.Select
                  value={paymentForm}
                  onChange={(e) => setPaymentForm(e.target.value)}
                  required
                >
                  {PAYMENT_FORM_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            {/* MONEDA */}
            <Col md={3}>
              <Form.Group controlId="currency">
                <Form.Label>
                  Moneda
                  <span className="text-danger ms-1">*</span>
                </Form.Label>
                <Form.Select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  required
                >
                  <option value="MXN">MXN - Pesos Mexicanos</option>
                  <option value="DLS">DLS - Dólares Americanos</option>
                </Form.Select>
              </Form.Group>
            </Col>

            {/* IMPORTE CON IVA (requerido) */}
            <Col md={3}>
              <Form.Group controlId="totalWithIva">
                <Form.Label>
                  Importe de la factura con IVA
                </Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  min="0"
                  value={totalWithIva}
                  onChange={(e) => setTotalWithIva(e.target.value)}
                  placeholder="0.00"
                />
              </Form.Group>
            </Col>

            {/* IVA (requerido) */}
            <Col md={3}>
              <Form.Group controlId="iva">
                <Form.Label>
                  IVA
                </Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  min="0"
                  value={iva}
                  onChange={(e) => setIva(e.target.value)}
                  placeholder="0.00"
                />
              </Form.Group>
            </Col>

            {/* DROPZONE ARCHIVO (requerido) */}
            <Col md={12}>
              <Form.Group controlId="attachment">
                <Form.Label>
                  Comprobante (PDF o imagen)
                  <span className="text-danger ms-1">*</span>
                </Form.Label>

                <div
                  {...getRootProps()}
                  className="border border-secondary rounded p-3 text-center"
                  style={{
                    cursor: "pointer",
                    backgroundColor: isDragActive ? "#f8f9fa" : "white",
                  }}
                >
                  <input {...getInputProps()} />
                  {isDragActive ? (
                    <p>Suelta el archivo aquí...</p>
                  ) : (
                    <p>
                      Arrastra y suelta un archivo aquí, o haz clic para
                      seleccionar.
                    </p>
                  )}
                </div>

                {attachment && (
                  <div className="mt-2 small">
                    <strong>Archivo seleccionado: </strong>
                    {attachment.name} ({Math.round(attachment.size / 1024)} KB)
                  </div>
                )}
              </Form.Group>
            </Col>

            {/* CONCEPTOS DE FACTURA (requerido) */}
            <Col md={12}>
              <Form.Group controlId="descriptionsAndBillingConcepts">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <Form.Label className="mb-0">Conceptos de la factura</Form.Label>
                  <div className="d-flex gap-2">
                    <a
                      href="/formato_factura.xlsx"
                      download
                      className="btn btn-success btn-sm"
                    >
                      <i className="bi bi-file-earmark-excel me-1" />
                      Descarga layout
                    </a>
                    <label className="btn btn-primary btn-sm mb-0">
                      <i className="bi bi-upload me-1" />
                      Sube tu layout
                      <input
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        hidden
                        onChange={(e) =>
                          setLayoutFile(e.target.files?.[0] || null)
                        }
                      />
                    </label>
                  </div>
                </div>
                {layoutFile && (
                  <div className="small text-muted mb-1">
                    Layout: {layoutFile.name}
                  </div>
                )}
                <Form.Control
                  as="textarea"
                  rows={6}
                  value={descriptionsAndBillingConcepts}
                  onChange={(e) =>
                    setDescriptionsAndBillingConcepts(e.target.value)
                  }
                  placeholder={
                    "Lista los conceptos a facturar. Por cada uno indica: descripción y unidad de medida.\n\nEjemplo:\n- Consultoría en TI — Análisis y diseño de sistema (Unidad: hora)\n- Renta de equipo de cómputo (Unidad: pieza)"
                  }
                  required
                />
                <Form.Text className="text-muted">
                  Describe qué se está facturando y la unidad de medida de cada
                  concepto.
                </Form.Text>
              </Form.Group>
            </Col>

            {/* NOTAS (requerido) */}
            <Col md={12}>
              <Form.Group controlId="notes">
                <Form.Label>Notas / Comentarios</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Información adicional para la factura (opcional)"
                />
              </Form.Group>
            </Col>
          </Row>

          <div className="mt-3 d-flex gap-2">
            <Button type="submit" variant="primary" disabled={submitting}>
              {submitting ? "Enviando solicitud..." : "Solicitar factura"}
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default CreateBill;
